/**
 * Language Learning form logic (P5), kept pure so it can be tested without a DOM.
 */
import type {
  CefrLevel,
  LanguageLearningCatalog,
  LanguageLearningRequest,
  LanguageRegister,
  LanguageScenarioEntry,
  LanguageSituation,
} from '../store/api/languageLearningApi';

export interface LanguageLearningFields {
  language: string | null;
  proficiency: CefrLevel | null;
  region: string | null;
  register: LanguageRegister | null;
  situation: LanguageSituation | null;
  scenarioId: string | null;
  objective: string;
}

export const EMPTY_LANGUAGE_FIELDS: LanguageLearningFields = {
  language: null, proficiency: null, region: null, register: null, situation: null, scenarioId: null, objective: '',
};

/**
 * The request to send. A blank objective is omitted (never sent as ""), a
 * region is sent only when one was chosen, and nothing else is added: the
 * client never names a persona or rubric.
 */
export function buildLanguageLearningRequest(fields: {
  language: string;
  proficiency: CefrLevel;
  region: string | null;
  register: LanguageRegister;
  situation: LanguageSituation;
  objective: string;
}): LanguageLearningRequest {
  const objective = fields.objective.trim();
  return {
    language: fields.language,
    proficiency: fields.proficiency,
    ...(fields.region ? { region: fields.region } : {}),
    register: fields.register,
    situation: fields.situation,
    ...(objective ? { objective } : {}),
  };
}

/** The scenarios offered for a situation (none when it is not authored). */
export function scenariosFor(catalog: LanguageLearningCatalog | undefined, situation: LanguageSituation | null): LanguageScenarioEntry[] {
  if (!catalog || !situation) return [];
  const entry = catalog.situations.find((s) => s.situation === situation);
  return entry && entry.status === 'available' ? entry.scenarios : [];
}

/**
 * Why the form cannot start yet, or null when it can. The server re-validates
 * everything; this only keeps the learner from sending what it would refuse.
 */
export function languageLearningBlocker(fields: LanguageLearningFields, catalog: LanguageLearningCatalog | undefined): string | null {
  if (!catalog) return 'Language practice is loading.';
  if (!fields.language) return 'Choose the language you are learning.';
  if (!fields.proficiency) return 'Choose your current level.';
  if (!fields.register) return 'Choose formal or informal.';
  if (!fields.situation) return 'Choose a situation.';
  const situation = catalog.situations.find((s) => s.situation === fields.situation);
  if (!situation || situation.status !== 'available') return 'Practice for this situation has not been authored yet.';
  if (!fields.scenarioId || !situation.scenarios.some((s) => s.id === fields.scenarioId)) return 'Choose a conversation to practise.';
  const regions = catalog.languages.find((l) => l.code === fields.language)?.regions ?? [];
  if (fields.region && !regions.some((r) => r.key === fields.region)) return 'Choose a region offered for this language.';
  if (fields.objective.trim().length > catalog.limits.objectiveMaxChars) {
    return `Your goal must be at most ${catalog.limits.objectiveMaxChars} characters.`;
  }
  return null;
}

/** A measured score, or the honest word for an unmeasured one. Never "0". */
export function displayMeasured(value: number | null): string {
  return value === null ? 'Not measured' : String(Math.round(value));
}

/** Signed change between first and latest measured values, or null. */
export function displayChange(change: number | null): string | null {
  if (change === null) return null;
  return change > 0 ? `+${change}` : String(change);
}
